import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { postsApi } from "@/services/posts";
import { Post, CreatePostData, UpdatePostData } from "@/types/posts";

interface PostsState {
  posts: Post[];
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  currentPost: Post | null;
  ordering: string | undefined;
  search: string | undefined;
  category: string | string[] | undefined;

  // Actions
  fetchPosts: (
    page?: number,
    ordering?: string,
    search?: string,
    category?: string | string[],
  ) => Promise<void>;
  fetchPost: (id: string) => Promise<void>;
  createPost: (data: CreatePostData) => Promise<void>;
  updatePost: (id: string, data: UpdatePostData) => Promise<void>;
  patchPost: (id: string, data: Partial<UpdatePostData>) => Promise<void>;
  deletePost: (id: string) => Promise<void>;
  likePost: (id: string) => Promise<void>;
  unlikePost: (id: string) => Promise<void>;
  reset: () => void;
  setOrdering: (ordering?: string) => void;
  setSearch: (search?: string) => void;
  setCategory: (category?: string | string[]) => void;
}

export const usePostsStore = create<PostsState>()(
  persist(
    (set, get) => ({
      posts: [],
      currentPage: 1,
      totalPages: 1,
      isLoading: false,
      error: null,
      currentPost: null,
      ordering: undefined,
      search: undefined,
      category: undefined,

      setOrdering: (ordering?: string) => {
        set({ ordering });
        get().fetchPosts(1, ordering, get().search, get().category);
      },

      setSearch: (search?: string) => {
        set({ search });
        get().fetchPosts(1, get().ordering, search, get().category);
      },

      setCategory: (category?: string | string[]) => {
        set({ category });
        get().fetchPosts(1, get().ordering, get().search, category);
      },

      fetchPosts: async (
        page = 1,
        ordering = get().ordering,
        search = get().search,
        category = get().category,
      ) => {
        try {
          set({ isLoading: true, error: null });
          const response = await postsApi.getPosts(
            page,
            ordering,
            search,
            category,
          );
          set({
            posts:
              page === 1 ? response.data : [...get().posts, ...response.data],
            currentPage: response.meta.currentPage,
            totalPages: response.meta.totalPages,
            ordering,
            search,
            category,
          });
        } catch {
          set({ error: "Failed to fetch posts" });
        } finally {
          set({ isLoading: false });
        }
      },

      fetchPost: async (id: string) => {
        try {
          set({ isLoading: true, error: null });
          const post = await postsApi.getPost(id);
          set({ currentPost: post });
        } catch {
          set({ error: "Failed to fetch post" });
        } finally {
          set({ isLoading: false });
        }
      },

      createPost: async (data: CreatePostData) => {
        try {
          set({ isLoading: true, error: null });
          const post = await postsApi.createPost(data);
          set((state) => ({
            posts: [post, ...state.posts],
          }));
        } catch {
          set({ error: "Failed to create post" });
          throw new Error("Failed to create post");
        } finally {
          set({ isLoading: false });
        }
      },

      updatePost: async (id: string, data: UpdatePostData) => {
        try {
          set({ isLoading: true, error: null });
          const updatedPost = await postsApi.updatePost(id, data);
          set((state) => ({
            posts: state.posts.map((post) =>
              post.id === id ? updatedPost : post,
            ),
            currentPost:
              state.currentPost?.id === id ? updatedPost : state.currentPost,
          }));
        } catch {
          set({ error: "Failed to update post" });
          throw new Error("Failed to update post");
        } finally {
          set({ isLoading: false });
        }
      },

      patchPost: async (id: string, data: Partial<UpdatePostData>) => {
        try {
          set({ isLoading: true, error: null });
          const updatedPost = await postsApi.patchPost(id, data);
          set((state) => ({
            posts: state.posts.map((post) =>
              post.id === id ? updatedPost : post,
            ),
            currentPost:
              state.currentPost?.id === id ? updatedPost : state.currentPost,
          }));
        } catch {
          set({ error: "Failed to update post" });
          throw new Error("Failed to update post");
        } finally {
          set({ isLoading: false });
        }
      },

      deletePost: async (id: string) => {
        try {
          set({ isLoading: true, error: null });
          await postsApi.deletePost(id);
          set((state) => ({
            posts: state.posts.filter((post) => post.id !== id),
            currentPost:
              state.currentPost?.id === id ? null : state.currentPost,
          }));
        } catch {
          set({ error: "Failed to delete post" });
          throw new Error("Failed to delete post");
        } finally {
          set({ isLoading: false });
        }
      },

      likePost: async (id: string) => {
        try {
          const updatedPost = await postsApi.likePost(id);
          set((state) => ({
            posts: state.posts.map((post) =>
              post.id === id ? updatedPost : post,
            ),
            currentPost:
              state.currentPost?.id === id ? updatedPost : state.currentPost,
          }));
        } catch {
          set({ error: "Failed to like post" });
        }
      },

      unlikePost: async (id: string) => {
        try {
          const updatedPost = await postsApi.unlikePost(id);
          set((state) => ({
            posts: state.posts.map((post) =>
              post.id === id ? updatedPost : post,
            ),
            currentPost:
              state.currentPost?.id === id ? updatedPost : state.currentPost,
          }));
        } catch {
          set({ error: "Failed to unlike post" });
        }
      },

      reset: () => {
        set({
          posts: [],
          currentPage: 1,
          totalPages: 1,
          isLoading: false,
          error: null,
          currentPost: null,
          ordering: undefined,
          search: undefined,
          category: undefined,
        });
      },
    }),
    {
      name: "posts-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        posts: state.posts,
        currentPage: state.currentPage,
        totalPages: state.totalPages,
        ordering: state.ordering,
        search: state.search,
        category: state.category,
      }),
    },
  ),
);
